import { DocumentReference, getDocFromServer, runTransaction } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { db, storage } from '../firebase';

export interface ReservationAttachments {
  applicationFormUrl?: string | null;
  applicationFormFileName?: string | null;
  applicationFormStoragePath?: string | null;
  fileUrl?: string | null;
  fileName?: string | null;
  fileStoragePath?: string | null;
}
export interface AttachmentChange { file: File | null; remove: boolean }
export interface AttachmentChanges { application: AttachmentChange; additional: AttachmentChange }
export const emptyAttachmentChanges = (): AttachmentChanges => ({
  application: { file: null, remove: false }, additional: { file: null, remove: false }
});
export function validateAttachment(file: File): string | null {
  if (!file.size) return '빈 파일은 첨부할 수 없습니다.';
  if (file.size > 5 * 1024 * 1024) return '파일별 5MB 이하만 첨부할 수 있습니다.';
  if (file.type && !/^(application|image)\//.test(file.type)) return '문서 또는 이미지 파일을 선택해주세요.';
  return null;
}
const keys = ['applicationFormUrl', 'applicationFormFileName', 'applicationFormStoragePath', 'fileUrl', 'fileName', 'fileStoragePath'] as const;
async function withTimeout<T>(operation: Promise<T>): Promise<T> {
  let timer: number | undefined;
  try {
    return await Promise.race([operation, new Promise<never>((_, reject) => {
      timer = window.setTimeout(() => reject(new Error('처리 시간이 초과되었습니다. 네트워크 상태를 확인해주세요.')), 90000);
    })]);
  } finally { window.clearTimeout(timer); }
}
async function removeFiles(paths: string[]): Promise<boolean> {
  const results = await Promise.all(paths.map(async path => {
    try { await withTimeout(deleteObject(ref(storage, path))); return true; }
    catch (error) { return (error as { code?: string }).code === 'storage/object-not-found'; }
  }));
  return results.every(Boolean);
}

// Upload first, atomically save references, then remove superseded objects.
export async function saveReservationWithAttachments(
  documentRef: DocumentReference, uid: string, previous: ReservationAttachments | null,
  changes: AttachmentChanges, data: Record<string, unknown>, onProgress: (message: string) => void
): Promise<{ attachments: ReservationAttachments; cleanupFailed: boolean }> {
  const attachments: ReservationAttachments = {};
  const uploaded: string[] = [];
  const obsolete: string[] = [];
  const mutationId = crypto.randomUUID();
  try {
    for (const kind of ['application', 'additional'] as const) {
      const change = changes[kind];
      if (!change.file && !change.remove) continue;
      const urlKey = kind === 'application' ? 'applicationFormUrl' : 'fileUrl';
      const nameKey = kind === 'application' ? 'applicationFormFileName' : 'fileName';
      const pathKey = kind === 'application' ? 'applicationFormStoragePath' : 'fileStoragePath';
      const oldPath = previous?.[pathKey] || previous?.[urlKey];
      if (oldPath) obsolete.push(oldPath);
      attachments[urlKey] = null; attachments[nameKey] = null; attachments[pathKey] = null;
      if (change.file) {
        const validation = validateAttachment(change.file);
        if (validation) throw new Error(validation);
        const label = kind === 'application' ? '대관신청서' : '추가 서류';
        const path = `reservations/${uid}/${kind === 'application' ? 'application-forms' : 'additional-documents'}/${documentRef.id}_${crypto.randomUUID()}`;
        const objectRef = ref(storage, path);
        uploaded.push(path);
        const task = uploadBytesResumable(objectRef, change.file, { contentType: change.file.type || 'application/octet-stream' });
        await new Promise<void>((resolve, reject) => {
          const timer = window.setTimeout(() => { task.cancel(); reject(new Error(`${label} 업로드 시간이 초과되었습니다.`)); }, 90000);
          task.on('state_changed', snapshot => onProgress(`${label} 업로드 중… ${Math.round(snapshot.bytesTransferred / snapshot.totalBytes * 100)}%`),
            error => { window.clearTimeout(timer); reject(error); },
            () => { window.clearTimeout(timer); resolve(); });
        });
        attachments[urlKey] = await withTimeout(getDownloadURL(objectRef));
        attachments[nameKey] = change.file.name; attachments[pathKey] = path;
      }
    }
    onProgress('예약 정보를 저장 중…');
    await runTransaction(db, async transaction => {
      const current = await transaction.get(documentRef);
      if (previous) {
        if (!current.exists()) throw new Error('예약이 삭제되었습니다. 목록을 새로고침해주세요.');
        if (keys.some(key => (current.data()[key] || null) !== (previous[key] || null))) {
          throw new Error('첨부파일이 다른 작업에서 변경되었습니다. 상세 화면을 새로고침해주세요.');
        }
        transaction.update(documentRef, { ...data, ...attachments, attachmentMutationId: mutationId });
      } else {
        if (current.exists()) throw new Error('이미 저장된 예약입니다. 목록을 확인해주세요.');
        transaction.set(documentRef, { ...data, ...attachments, attachmentMutationId: mutationId });
      }
    });
  } catch (error) {
    // Resolve uncertain commits before cleaning uploads or permitting a retry.
    let committed = false;
    try {
      const current = await withTimeout(getDocFromServer(documentRef));
      committed = current.data()?.attachmentMutationId === mutationId;
      if (!committed) {
        const referenced = [current.data()?.fileStoragePath, current.data()?.applicationFormStoragePath];
        await removeFiles(uploaded.filter(path => !referenced.includes(path)));
      }
    } catch { /* Keep objects when commit status cannot be verified. */ }
    if (!committed) throw error;
  }
  if (obsolete.length) onProgress('이전 첨부파일 정리 중…');
  return { attachments, cleanupFailed: !(await removeFiles(obsolete)) };
}
